import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useClientAuth } from "@/lib/clientAuth";
import GoogleSignInButton from "@/components/site/GoogleSignInButton";
import { LogIn, ArrowRight } from "lucide-react";

export default function ClientLogin() {
  const { login } = useClientAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get("err") === "google") {
      setErr("Google sign-in was cancelled or failed. Please try again.");
    }
  }, [searchParams, setErr]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password);
      nav("/portal");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="client-login-page" className="min-h-screen pt-24 pb-24 bg-brand-bg flex items-center">
      <div className="max-w-md w-full mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-6">
            <span className="h-10 w-10 flex items-center justify-center bg-primary-brand text-white">
              <LogIn className="h-5 w-5" strokeWidth={2.5} />
            </span>
            <div>
              <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand">Client Portal</div>
              <h1 className="font-heading text-2xl font-black text-slate-900">Sign in</h1>
            </div>
          </div>
          <form onSubmit={submit} className="border border-slate-200 bg-white p-8">
            <GoogleSignInButton testid="client-login-google" label="Continue with Google" />
            <div className="my-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] font-bold text-slate-400">
              <span className="h-px flex-1 bg-slate-200" />
              or
              <span className="h-px flex-1 bg-slate-200" />
            </div>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">Email</div>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="client-login-email"
                className="w-full bg-transparent border border-slate-200 focus:border-primary-brand outline-none px-4 py-3 text-slate-900 text-sm"
              />
            </label>
            <label className="block mt-4">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">Password</div>
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="client-login-password"
                className="w-full bg-transparent border border-slate-200 focus:border-primary-brand outline-none px-4 py-3 text-slate-900 text-sm"
              />
            </label>
            {err && (
              <div data-testid="client-login-error" className="mt-4 border border-red-300 bg-red-50 text-red-700 px-3 py-2 text-xs">
                {err}
              </div>
            )}
            <button
              type="submit" disabled={loading}
              data-testid="client-login-submit"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-primary-brand hover:bg-[#E04F00] disabled:opacity-60 text-white px-6 py-3 font-bold uppercase tracking-wider text-sm"
            >
              {loading ? "Signing in…" : "Sign in"} <ArrowRight className="h-4 w-4" />
            </button>
          </form>
          <div className="mt-6 text-sm text-slate-600 text-center">
            New to Laronta Pro?{" "}
            <Link to="/portal/register" data-testid="client-login-register-link" className="text-primary-brand font-bold hover:underline">
              Create an account
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
